import React, { useState } from 'react';
import Text from '../../../Text';
import Toggle from '../../../Transition/Toggle';
import AccordionItemCaret from '../AccordionItemCaret';
import { timingMap } from '../../../Transition/constants';
import { dataHooks } from '../../constants';

import { st, classes } from './AccordionItem.st.css';
import {
  AccordionItemCaretProps,
  AccordionItemProps,
} from '../../Accordion.types';

type IconsContainerProps = {
  position: 'start' | 'end';
  toggleIconPlacement: AccordionItemProps['toggleIconPlacement'];
  icon: React.ReactNode;
  iconDataHook?: string;
  caretProps: AccordionItemCaretProps;
};

const IconsContainer = ({
  position,
  toggleIconPlacement,
  icon,
  iconDataHook,
  caretProps,
}: IconsContainerProps) => {
  const shouldShowToggleIcon = toggleIconPlacement === position;

  return shouldShowToggleIcon || icon ? (
    <div className={classes.iconsContainer}>
      {shouldShowToggleIcon && position === 'start' && (
        <AccordionItemCaret {...caretProps} />
      )}
      {icon && (
        <div className={classes.affix} data-hook={iconDataHook}>
          {icon}
        </div>
      )}
      {shouldShowToggleIcon && position === 'end' && (
        <AccordionItemCaret {...caretProps} />
      )}
    </div>
  ) : null;
};

function AccordionItem({
  icon,
  prefix,
  suffix,
  title,
  subtitle,
  open,
  children,
  onToggle,
  onMouseLeave,
  onMouseEnter,
  disabled,
  skin,
  hideShadow,
  className,
  size,
  horizontalPadding,
  contentPadding,
  last,
  transitionSpeed = 'medium',
  onAnimationEnter,
  onAnimationExit,
  buttonType = 'textButton',
  expandLabel,
  collapseLabel,
  toggleIconPlacement = 'end',
  showLabel,
  titleSize,
}: AccordionItemProps) {
  // needed for toggle button label visibility
  const [hover, setHover] = useState(false);

  const caretProps = {
    hover,
    buttonType,
    expandLabel,
    collapseLabel,
    showLabel,
    open,
    disabled,
    skin,
  };

  const _onMouseLeave = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setHover(false);
    !disabled && onMouseLeave && onMouseLeave(e);
  };

  const _onMouseEnter = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setHover(true);
    !disabled && onMouseEnter && onMouseEnter(e);
  };

  return (
    <div
      data-hook={dataHooks.item}
      className={st(
        classes.root,
        {
          disabled,
          open,
          skin,
          hideShadow,
          size,
          horizontalPadding,
          contentPadding,
          last,
          titleSize,
        },
        className,
      )}
    >
      <div
        onClick={!disabled ? onToggle : () => {}}
        className={st(classes.header, { size })}
        data-hook={dataHooks.header}
        onMouseEnter={_onMouseEnter}
        onMouseLeave={_onMouseLeave}
      >
        <IconsContainer
          position="start"
          toggleIconPlacement={toggleIconPlacement}
          icon={prefix || icon}
          caretProps={caretProps}
          iconDataHook={dataHooks.prefix}
        />
        <div className={classes.titleWrapper}>
          {title && (
            <div data-hook="titleContainer">
              {typeof title === 'string' ? (
                <Text
                  dataHook="title"
                  ellipsis
                  weight="normal"
                  className={classes.title}
                >
                  {title}
                </Text>
              ) : (
                title
              )}
            </div>
          )}
          {subtitle && (
            <div data-hook={dataHooks.subtitle}>
              {typeof subtitle === 'string' ? (
                <Text size="small">{subtitle}</Text>
              ) : (
                subtitle
              )}
            </div>
          )}
        </div>
        <IconsContainer
          position="end"
          toggleIconPlacement={toggleIconPlacement}
          icon={suffix}
          caretProps={caretProps}
          iconDataHook={dataHooks.suffix}
        />
      </div>
      <Toggle
        show={open}
        timeout={timingMap[transitionSpeed]}
        onStart={onAnimationEnter}
        onEnd={onAnimationExit}
        unmountOnExit
      >
        <div data-hook="children" className={classes.children}>
          {children}
        </div>
      </Toggle>
    </div>
  );
}

export default AccordionItem;
